import { useEffect, useMemo, useState, Suspense, useRef } from "react";
import { useRouteLoaderData, Await } from "react-router-dom";
import { Button, Col, Container, Modal, Stack } from "react-bootstrap";
import { Youtube } from "react-bootstrap-icons";
import { axiosInstance } from "../../auth/Auth";
import useSortableData from "../useSortableData";
import moment from "moment";
import ErrorResponse from "../ErrorResponse";
import LoadingIndicator from "../LoadingIndicator";
import TrendOverTime from "../TrendOverTime";

import ExportData from "../ExportData";
import SongSearchInput from "../SearchInput";
import { useResolvedModules, useResolvedRadioNames } from "../HelperHooks";
import SongCatalogueHeader from "./SongCatalogueHeader";
import SongCatalogueRow from "./SongCatalogueRow";
import BackToTopButton from "../BackToTopButton";
import SongHistoryModal from "../songHistory/SongHistoryModal";
import { getRadioName } from "../HelperFunctions";
import EmptyRow from "../EmptyRow";

export default function SongCatalogueModal({ show, onHide, song }) {
    const { radios } = useRouteLoaderData("root");

    const [data, setData] = useState(null);
    const [trendRadios, setTrendRadios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorCode, setErrorCode] = useState(null);

    const [selectedTrendDataType, setSelectedTrendDataType] = useState(null);
    const [selectedRadioId, setSelectedRadioId] = useState(null);
    const [showSongHistory, setShowHistory] = useState(false);
    const [showTrend, setShowTrend] = useState(false);

    const containerRef = useRef();

    const resolvedRadios = useResolvedRadioNames();
    const resolvedModules = useResolvedModules();

    const musicEnabled = useMemo(() => resolvedModules?.includes(5), [resolvedModules]);
    const musicTestingEnabled = useMemo(() => resolvedModules?.includes(7), [resolvedModules]);

    const [filterData, setFilterData] = useState({ searchString: "" });

    useEffect(() => {
        async function FetchTrendData() {
            const response = await axiosInstance.get(`music_test/${song.item_id}/item_on_radios?daypart_id=1&demo_id=1&months=8`);
            setTrendRadios(response.data);
        }
        if (song && show && musicEnabled) FetchTrendData();
    }, [song, show, musicEnabled]);

    useEffect(() => {
        async function FetchData() {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`items/${song.item_id}/radio_station_catalog/`);
                setData(response.data);
                setErrorCode(null);
            } catch (error) {
                setData(null);
                setErrorCode(error?.response?.status ?? 500);
            }
            setLoading(false);
        }
        if (song && show && musicEnabled) FetchData();
    }, [show, song, musicEnabled]);

    const dataMemo = useMemo(() => {
        if (data) {
            let filteredData = data;
            if (filterData.searchString) {
                filteredData = filteredData.filter(
                    x =>
                        getRadioName(x.radio_id, resolvedRadios).toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        moment(x.first_play).format("L")?.toString().toLowerCase().includes(filterData.searchString.trim()) ||
                        x.total_spins?.toString().includes(filterData.searchString.trim())
                );
            }
            return filteredData;
        }
    }, [data, filterData.searchString, resolvedRadios]);

    async function openYoutube() {
        const searchString = encodeURIComponent(`${song?.artist} - ${song?.title}`);
        const url = "https://www.youtube.com/results?search_query=" + searchString;
        window.open(url, "_blank");
    }

    const { sortedItems, requestSort, sortConfig } = useSortableData(dataMemo, { key: "total_spins", direction: "descending" });
    const csvHeaders = ["Station", "This Week", "Week -1", "Week -2", "Week -3", "Total Spins", "First Time"];
    const csvData = useMemo(() => {
        if (!sortedItems || !resolvedRadios) return [];
        return sortedItems.map(x => [
            resolvedRadios?.find(r => r.radio_id === x.radio_id)?.radio_name ?? "Unknown",
            x.weeks[3].spins,
            x.weeks[2].spins,
            x.weeks[1].spins,
            x.weeks[0].spins,
            x.total_spins,
            moment(x.first_time).format("L HH:mm"),
        ]);
    }, [resolvedRadios, sortedItems]);

    return (
        <>
            <Modal show={show && !showTrend && !showSongHistory && musicEnabled} onHide={onHide} size="xl" centered scrollable>
                <Modal.Header closeButton className=" py-1 py-xl-3">
                    <Modal.Title className=" text-uppercase">Song Catalogue</Modal.Title>
                </Modal.Header>
                <Modal.Body className="d-flex flex-column pt-0 row-gap-2" ref={containerRef}>
                    {loading ? (
                        <LoadingIndicator width={75} />
                    ) : errorCode ? (
                        <ErrorResponse errorCode={errorCode} />
                    ) : (
                        data && (
                            <>
                                <BackToTopButton container={containerRef} />
                                <Stack direction="horizontal" className="flex-wrap mt-1" gap={1}>
                                    <span>Stations playing </span>
                                    <span className="fw-bold">{`${song?.artist} - ${song?.title}`}</span>
                                    <Button variant={"contained"} onClick={openYoutube} className="p-0 btn-download text-yt-red">
                                        <Youtube width={35} height={35} />
                                    </Button>
                                </Stack>
                                <Stack direction="horizontal" gap={3} className="flex-wrap-reverse">
                                    <Col xs xl={5}>
                                        <SongSearchInput
                                            filterData={filterData}
                                            setFilterData={setFilterData}
                                            placeholder="Search by station, spins or first played"
                                        />
                                    </Col>
                                    <Col xs={"auto"} className="ms-auto">
                                        <Stack direction="horizontal" gap={1}>
                                            <span className="me-1">Download:</span>
                                            <ExportData data={csvData} headers={csvHeaders} filename={`Song Catalogue for ${song?.artist} - ${song?.title}`} />
                                        </Stack>
                                    </Col>
                                </Stack>
                                <div>
                                    <SongCatalogueHeader requestSort={requestSort} sortConfig={sortConfig} />
                                    <Container fluid>
                                        <Suspense>
                                            <Await resolve={radios}>
                                                {rRadios => (
                                                    <>
                                                        {sortedItems.map(x => {
                                                            const radio = rRadios.data.find(r => r.radio_id === x.radio_id);
                                                            return (
                                                                <SongCatalogueRow
                                                                    key={x.radio_id}
                                                                    item={x}
                                                                    setShowHistory={setShowHistory}
                                                                    setShowTrend={setShowTrend}
                                                                    setSelectedRadioId={() => setSelectedRadioId(x.radio_id)}
                                                                    trendDisabled={
                                                                        !trendRadios?.includes(x.radio_id) || radio?.mtt_only || !musicTestingEnabled
                                                                    }
                                                                    setSelectedTrendDataType={() => setSelectedTrendDataType(radio?.stream ? 1 : 2)}
                                                                />
                                                            );
                                                        })}
                                                        {!sortedItems?.length && <EmptyRow />}
                                                    </>
                                                )}
                                            </Await>
                                        </Suspense>
                                    </Container>
                                </div>
                            </>
                        )
                    )}
                </Modal.Body>
            </Modal>
            <TrendOverTime
                show={showTrend && !!song}
                onHide={() => setShowTrend(false)}
                radioId={selectedRadioId}
                itemId={song?.item_id}
                dataType={selectedTrendDataType}
                daypartId={1}
                monthsLimit={8}
            />
            <SongHistoryModal show={showSongHistory} onHide={() => setShowHistory(false)} song={song} radioId={selectedRadioId} />
        </>
    );
}

export function useSongCatalogueModal() {
    const [catalogueSong, setCatalogueSong] = useState(null);

    const SongCatalogueModalComponent = () => <SongCatalogueModal show={!!catalogueSong} onHide={() => setCatalogueSong(null)} song={catalogueSong} />;

    return { setCatalogueSong, SongCatalogueModalComponent };
}
